
//
// Helper for pushing preview for restaurant or meal
//
export function useOpenPreview(restaurantId, mealIds = undefined) {
  const route = useRoute()

  navigateTo({
    query: {
      ...route.query,
      restId: restaurantId, 
      mealIds: mealIds,
    }
  })
}

//
// Image helper for cloudinary
//
export function useCloudinaryImage(picture, width, height, crop = 'fill', source = 'upload') {
  const imageUrl = 'https://res.cloudinary.com/foodfriends/image'
  const format = 'f_auto'
  const quality = 'q_auto'
  const path = source === 'upload' ? `restaurant/${picture}` : picture
  const sizes = width && height 
    ? `w_${width},h_${height}` 
    : (width)
      ? `w_${width}`
      : (height)
        ? `h_${height}`
        : ''
  return `${imageUrl}/${source}/${sizes},c_${crop},${format},${quality}/${path}`
}

//
// Flatten restaurant tags if they are nested (aka restaurant page)
//
export function useRestaurantTags(tags) {
  if (!tags[0]?.tags) return tags

  return tags?.flatMap(group =>
    group.tags.map(tag => ({ ...tag, groupId: group.id }))
  )
}

//
// Get featured tags for restaurant
//
export function useFeaturedTags(tags, limit = 3) {
  const flatTags = useRestaurantTags(tags)
  const cuisines = flatTags.filter(tag => tag.groupId === 2) || []
  const cuisine = cuisines?.filter(tag => tag.id !== 83) || []
  const tagsGroup3 = flatTags.filter(tag => tag.groupId === 3) || []

  return ([
    ...cuisine?.slice(0, 1), 
    ...tagsGroup3
  ]).slice(0, limit)
}

//
// Filter restaurants based on search string and rank
//
export function useFatSearchFilter(restaurant, search) {
  const str = useNormalizeString(search || '')
  const name = useNormalizeString(restaurant?.name || '')
  const rank = restaurant.rank
  
  if (str.length === 0) true
  if (str.length === 1) return name.startsWith(str) 
  if (str.length === 2) return (name.includes(str) && rank > 120) || name.startsWith(str)
  
  return name.includes(str)
}

//
// Use pretty date range based on start and end date
//
export function usePrettyDateRange(startDate, endDate, format = 'dddd D MMMM') {
  const dayjs = useDayjs()

  if (dayjs(startDate).isSame(endDate, 'day')) {
    return useUcFirst(dayjs(startDate).format(format))
  }

  if (dayjs(startDate).isSame(endDate, 'month')) {
    return dayjs(startDate).format('D') + ' - ' + dayjs(endDate).format('D MMMM')
  }

  return dayjs(startDate).format('D MMMM') + ' - ' + dayjs(endDate).format('D MMMM')
}

//
// Use pretty time range based on start and end time
//
export function usePrettyTimeRange(startTime, endTime) {
  if (startTime !== endTime) {
    return `${startTime}-${endTime}`
  }
  
  return startTime
}

//
// Use pretty amount range
//
export function usePrettyAmountRange(minAmount, maxAmount) {
  const { t } = useI18n()

  if (minAmount !== maxAmount) {
    return `${minAmount}-${maxAmount} ${t('shared.amount')}`
  }
  
  return t('shared.amount', maxAmount)
}

//
// Show date according to weekday and date
//
export function useCalendarDate(date) {
  const { t } = useI18n()
  const dayjs = useDayjs()

  const isToday = dayjs(date).isToday() 
  const isTomorrow = dayjs(date).add(1, 'day').isTomorrow()
  const isSameWeek = dayjs(date).isSame(dayjs(), 'week')
  
  if (isToday) {
    return useUcFirst(t('shared.today'))
  } else if (isTomorrow) {
    return useUcFirst(t('shared.tomorrow'))
  } else if (isSameWeek) {
    return useUcFirst(dayjs(date).locale(useEnv('locale')).format('dddd'))
  }

  return useUcFirst(dayjs(date).locale(useEnv('locale')).format('dddd - D MMM'))
}

//
// Get available weekdays based on disabled days
//
export function useAvailableWeekdays(disabledDays) {
  const { t } = useI18n()

  // Get available days by filtering out disabled days
  const availableDays = [1, 2, 3, 4, 5, 6, 7].filter(day => !disabledDays?.includes(day))

  // If no disabledDays are set
  if (!disabledDays) {
    return 
  }

  // If no days are disabled, return 'all days'
  if (disabledDays.length === 0) {
    return t('shared.allDays')
  }

  // If only one day is available, return that day
  if (disabledDays.length === 6) {
    return t(`shared.days.${availableDays[0]}`)
  }

  // If one day is disabled (not monday and sunday)
  if (disabledDays.length === 1 && ![1, 2].includes(disabledDays[0])) {
    const disabledWeekday = t(`shared.days.${disabledDays[0]}`)?.toLowerCase()
    t('shared.allDaysExcept', { day: disabledWeekday })
  }

  // Handle multiple specific cases for disabled days using a lookup object
  const ranges = {
    '7': '1-6',          // Monday-Saturday
    '1': '2-7',          // Tuesday-Sunday
    '6,7': '1-5',        // Monday-Friday
    '1,7': '2-6',        // Tuesday-Saturday
    '1,2,3,4,5': '6-7',  // Saturday-Sunday
    '1,2,3,4': '5-7',    // Friday-Sunday
    '5,6,7': '1-4',      // Monday-Thursday
    '3,4,5,6,7': '1-2',  // Monday-Tuesday
    '4,5,6,7': '1-3',    // Monday-Wednesday
    '1,2,3,7': '2-6',    // Thursday-Saturday
    '1,2,7': '3-6',      // Wednesday-Saturday
  }

  // Sort the disabled days to match the key format in the ranges object
  const key = disabledDays.sort((a, b) => a - b)?.join(',')
  
  // Return the corresponding range if it exists
  if (ranges[key]) {
    return ranges[key]?.split('-')?.map(d => t(`shared.days.${d}`))?.join('-')
  }

  // Return all available days as fallback
  return availableDays?.map(day => t(`shared.days.${day}`))?.join(', ')
}